import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createCultivationClosePackSchedule,
  disableCultivationClosePackSchedule,
  downloadCultivationClosePack,
  downloadCultivationClosePackSchedule,
  enableCultivationClosePackSchedule,
  listCultivationClosePackSchedules,
  type CultivationClosePackSchedule,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { CultivationCapabilityNotice } from "./CultivationCapabilityNotice";
import { capabilityAllowed } from "./cultivation-capabilities";
import type { CultivationWorkspace } from "./cultivation-types";

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function scheduleStatusTone(status: CultivationClosePackSchedule["last_status"]): "success" | "danger" | "neutral" | "warning" {
  if (status === "ready") return "success";
  if (status === "failed") return "danger";
  return "neutral";
}

export function CultivationPack({
  workspace,
  showSchedules = true,
}: {
  workspace: CultivationWorkspace;
  showSchedules?: boolean;
}) {
  const { scope } = useOrganization();
  const [schedules, setSchedules] = useState<CultivationClosePackSchedule[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState("5");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canConfigure = capabilityAllowed(workspace.capabilities, "configure");
  const canView = capabilityAllowed(workspace.capabilities, "view");
  const packAvailable = workspace.reviews.some(
    (review) => review.state === "posted" || review.state === "locked",
  );
  const schedulesToken = useRef(0);
  const scopeKey = [scope?.legal_entity_id ?? "", workspace.licenseId].join("|");
  const scopeKeyRef = useRef(scopeKey);
  scopeKeyRef.current = scopeKey;

  const reload = useCallback(async () => {
    const capturedScope = scopeKey;
    const token = ++schedulesToken.current;
    if (!showSchedules || !canView || !scope?.legal_entity_id || !workspace.licenseId) {
      setSchedules([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await listCultivationClosePackSchedules({
        legal_entity_id: scope.legal_entity_id,
        license_id: workspace.licenseId,
      });
      if (token !== schedulesToken.current || scopeKeyRef.current !== capturedScope) return;
      setSchedules(result.schedules);
    } catch (cause) {
      if (token === schedulesToken.current && scopeKeyRef.current === capturedScope) {
        setError(messageOf(cause));
      }
    } finally {
      if (token === schedulesToken.current && scopeKeyRef.current === capturedScope) {
        setLoading(false);
      }
    }
  }, [canView, scope?.legal_entity_id, scopeKey, showSchedules, workspace.licenseId]);

  useEffect(() => {
    setSchedules([]);
    void reload();
  }, [reload]);

  async function downloadPack() {
    if (!scope?.legal_entity_id || !workspace.licenseId) return;
    setBusy("pack");
    setError(null);
    setNotice(null);
    try {
      await downloadCultivationClosePack({
        legal_entity_id: scope.legal_entity_id,
        license_id: workspace.licenseId,
        period_start: workspace.periodStart,
        period_end: workspace.periodEnd,
      });
      setNotice("Cultivation close pack ZIP downloaded.");
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  }

  async function createSchedule() {
    if (!scope?.legal_entity_id || !workspace.licenseId) return;
    const day = Number(dayOfMonth);
    if (!Number.isInteger(day) || day < 1 || day > 31) {
      setError("Choose a day between 1 and 31.");
      return;
    }
    setBusy("create");
    setError(null);
    setNotice(null);
    try {
      await createCultivationClosePackSchedule({
        legal_entity_id: scope.legal_entity_id,
        license_id: workspace.licenseId,
        day_of_month: day,
        enabled: true,
      });
      setNotice(`Monthly pack schedule created for day ${day}.`);
      await reload();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  }

  async function toggleSchedule(schedule: CultivationClosePackSchedule) {
    setBusy(schedule.schedule_id);
    setError(null);
    setNotice(null);
    try {
      if (schedule.enabled) {
        await disableCultivationClosePackSchedule(schedule.schedule_id);
        setNotice("Pack schedule disabled.");
      } else {
        await enableCultivationClosePackSchedule(schedule.schedule_id);
        setNotice("Pack schedule enabled.");
      }
      await reload();
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  }

  async function downloadSchedule(schedule: CultivationClosePackSchedule) {
    setBusy(schedule.schedule_id);
    setError(null);
    setNotice(null);
    try {
      await downloadCultivationClosePackSchedule(schedule.schedule_id);
      setNotice("Latest scheduled pack ZIP downloaded.");
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-5" aria-label="Cultivation close report pack">
      <div className="flex flex-wrap items-center gap-2">
        <div>
          <h3 className="text-sm font-semibold">Report pack</h3>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Download the frozen cultivation close ZIP for {workspace.periodStart}–{workspace.periodEnd}, or schedule a monthly snapshot.
          </p>
        </div>
        <Badge className="ms-auto" tone={canView ? "success" : "warning"}>{canView ? "view" : "view denied"}</Badge>
      </div>
      {!canView ? <CultivationCapabilityNotice capability="view" action="download the close report pack" /> : null}
      {canView && packAvailable ? (
        <Button type="button" variant="secondary" onClick={() => void downloadPack()} disabled={busy !== null || !workspace.licenseId}>
          {busy === "pack" ? "Preparing ZIP…" : "Download pack ZIP"}
        </Button>
      ) : canView ? (
        <p className="text-xs text-[var(--muted-foreground)]">
          The ZIP becomes available after the close is posted.
        </p>
      ) : null}

      {showSchedules ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="grid gap-1 text-xs font-medium">
              Schedule day of month
              <input
                aria-label="Schedule day of month"
                type="number"
                min={1}
                max={31}
                className="min-h-10 w-28 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
                value={dayOfMonth}
                onChange={(event) => setDayOfMonth(event.target.value)}
                disabled={!canConfigure || busy !== null}
              />
            </label>
            <Button type="button" onClick={() => void createSchedule()} disabled={!canConfigure || busy !== null || loading || !workspace.licenseId}>
              {busy === "create" ? "Creating…" : "Add monthly schedule"}
            </Button>
            <span className="text-xs text-[var(--muted-foreground)]">The schedule generates once per period and keeps the latest pack for in-app download.</span>
          </div>
          {!canConfigure ? <CultivationCapabilityNotice capability="configure" action="create, enable, or disable pack schedules" /> : null}
          {loading ? <p role="status" className="text-sm text-[var(--muted-foreground)]">Loading pack schedules…</p> : null}
          {!loading && schedules.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">No pack schedules exist for this location.</p>
          ) : null}
          {schedules.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Day</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Enabled</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Next run</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Last run</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Status</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Hash</th>
                    <th className="border-b border-[var(--border)] px-2 py-1 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((schedule) => (
                    <tr key={schedule.schedule_id}>
                      <td className="px-2 py-1">{schedule.day_of_month}</td>
                      <td className="px-2 py-1"><Badge tone={schedule.enabled ? "success" : "neutral"}>{schedule.enabled ? "enabled" : "disabled"}</Badge></td>
                      <td className="px-2 py-1">{schedule.next_run_on ?? "—"}</td>
                      <td className="px-2 py-1">{schedule.last_run_on ?? "—"}</td>
                      <td className="px-2 py-1">
                        <Badge tone={scheduleStatusTone(schedule.last_status)}>{schedule.last_status ?? "not run"}</Badge>
                        {schedule.last_error ? <p className="mt-1 max-w-xs text-xs text-[var(--danger)]">{schedule.last_error}</p> : null}
                      </td>
                      <td className="px-2 py-1 font-mono text-xs">{schedule.last_pack_sha256 ? `${schedule.last_pack_sha256.slice(0, 12)}…` : "—"}</td>
                      <td className="px-2 py-1">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            aria-label={`${schedule.enabled ? "Disable" : "Enable"} pack schedule for day ${schedule.day_of_month}`}
                            onClick={() => void toggleSchedule(schedule)}
                            disabled={!canConfigure || busy !== null || loading}
                          >
                            {schedule.enabled ? "Disable" : "Enable"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label={`Download pack for day ${schedule.day_of_month} schedule`}
                            onClick={() => void downloadSchedule(schedule)}
                            disabled={!canView || !schedule.download_available || busy !== null || loading}
                          >
                            Download
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <p role="alert" className="text-sm text-[var(--danger)]">{error}</p> : null}
      {notice ? <p role="status" className="text-sm text-[var(--muted-foreground)]">{notice}</p> : null}
    </section>
  );
}
